import { ISO_CODES_VALUES, LANGUAGE_VALUES } from '@bikdotai/bik-models/dm';

export class LanguageUtils {
  getStoreLanguage(isoCode: ISO_CODES_VALUES) {
    return Object.values(LANGUAGE_VALUES).includes(ISO_CODES_VALUES[isoCode])
      ? ISO_CODES_VALUES[isoCode]
      : ISO_CODES_VALUES.en;
  }

  getLanguageDirection(language: LANGUAGE_VALUES) {
    return language === LANGUAGE_VALUES.ARABIC ? 'rtl' : 'ltr';
  }

  private getValidationMessage(
    language: LANGUAGE_VALUES,
    messages: { [key in LANGUAGE_VALUES]?: string },
    defaultMessage: string,
  ) {
    return messages[language] || defaultMessage;
  }

  getEmailValidationMessage(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'يرجى إدخال عنوان بريد إلكتروني صالح',
        [LANGUAGE_VALUES.FRENCH]: 'Veuillez entrer une adresse e-mail valide',
        [LANGUAGE_VALUES.SPANISH]:
          'Por favor, introduce una dirección de correo electrónico válida',
        [LANGUAGE_VALUES.MALAY]: 'Sila masukkan alamat emel yang sah',
      },
      'Please enter a valid email address',
    );
  }

  getPhoneNumberValidationMessage(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'يرجى إدخال رقم هاتف صالح',
        [LANGUAGE_VALUES.FRENCH]:
          'Veuillez entrer un numéro de téléphone valide',
        [LANGUAGE_VALUES.SPANISH]:
          'Por favor, introduce un número de teléfono válido',
        [LANGUAGE_VALUES.MALAY]: 'Sila masukkan nombor telefon yang sah',
      },
      'Please enter a valid phone number',
    );
  }

  getRequiredFieldMessage(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'هذه الخانة مطلوبه',
        [LANGUAGE_VALUES.FRENCH]: 'Ce champ est requis',
        [LANGUAGE_VALUES.SPANISH]: 'Este campo es obligatorio',
        [LANGUAGE_VALUES.MALAY]: 'Ruangan ini diperlukan',
      },
      'This field is required',
    );
  }

  getPrivacyPolicyAcceptanceMessage(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'يرجى قبول سياسة الخصوصية للمتابعة',
        [LANGUAGE_VALUES.FRENCH]:
          'Veuillez accepter la politique de confidentialité pour continuer',
        [LANGUAGE_VALUES.SPANISH]:
          'Por favor, acepte la política de privacidad para continuar',
        [LANGUAGE_VALUES.MALAY]: 'Sila terima dasar privasi untuk meneruskan',
      },
      'Please accept the privacy policy to proceed',
    );
  }

  getOptionalEmailTitle(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'البريد الإلكتروني (اختياري)',
        [LANGUAGE_VALUES.FRENCH]: 'E-mail (facultatif)',
        [LANGUAGE_VALUES.SPANISH]: 'Correo electrónico (opcional)',
        [LANGUAGE_VALUES.MALAY]: 'E-mel (Pilihan)',
      },
      'Email (Optional)',
    );
  }

  getOptionalPhoneNumberTitle(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'رقم الهاتف (اختياري)',
        [LANGUAGE_VALUES.FRENCH]: 'Numéro de téléphone (facultatif)',
        [LANGUAGE_VALUES.SPANISH]: 'Número de teléfono (opcional)',
        [LANGUAGE_VALUES.MALAY]: 'Nombor telefon (Pilihan)',
      },
      'Phone Number (Optional)',
    );
  }

  getOptionalWhatsappNumberTitle(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'رقم الواتساب (اختياري)',
        [LANGUAGE_VALUES.FRENCH]: 'Numéro WhatsApp (facultatif)',
        [LANGUAGE_VALUES.SPANISH]: 'Número de WhatsApp (opcional)',
        [LANGUAGE_VALUES.MALAY]: 'Nombor WhatsApp (Pilihan)',
      },
      'Whatsapp Number (Optional)',
    );
  }

  getPoweredByMessage(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'مشغل بواسطة',
        [LANGUAGE_VALUES.FRENCH]: 'Propulsé par',
        [LANGUAGE_VALUES.SPANISH]: 'Desarrollado por',
        [LANGUAGE_VALUES.MALAY]: 'Dikuasakan oleh',
      },
      'Powered by',
    );
  }

  getEmailPlaceholder(language: LANGUAGE_VALUES, skipLabel: boolean) {
    const placeholders = {
      [LANGUAGE_VALUES.ARABIC]: skipLabel
        ? 'أدخل بريدك الإلكتروني'
        : 'مثل: abc@gmail.com',
      [LANGUAGE_VALUES.FRENCH]: skipLabel
        ? 'Entrez votre email'
        : 'ex: abc@gmail.com',
      [LANGUAGE_VALUES.SPANISH]: skipLabel
        ? 'Introduce tu correo electrónico'
        : 'ej: abc@gmail.com',
      [LANGUAGE_VALUES.MALAY]: skipLabel
        ? 'Masukkan e-mel anda'
        : 'cth: abc@gmail.com',
    };
    return (
      placeholders[language] ||
      (skipLabel ? 'Enter your email' : 'eg: abc@gmail.com')
    );
  }

  getPhoneNumberPlaceholder(language: LANGUAGE_VALUES) {
    const placeholders = {
      [LANGUAGE_VALUES.ARABIC]: 'مثل: 9876543210',
      [LANGUAGE_VALUES.FRENCH]: 'ex: 9876543210',
      [LANGUAGE_VALUES.SPANISH]: 'ej: 9876543210',
      [LANGUAGE_VALUES.MALAY]: 'cth: 9876543210',
    };
    return placeholders[language] || 'eg: 9876543210';
  }

  getCountryPlaceholder(language: LANGUAGE_VALUES) {
    const placeholders = {
      [LANGUAGE_VALUES.ARABIC]: 'البلد',
      [LANGUAGE_VALUES.FRENCH]: 'Pays',
      [LANGUAGE_VALUES.SPANISH]: 'País',
      [LANGUAGE_VALUES.MALAY]: 'Negara',
    };
    return placeholders[language] || 'Country';
  }

  getWhatsappNumberPlaceholder(language: LANGUAGE_VALUES, skipLabel: boolean) {
    const placeholders = {
      [LANGUAGE_VALUES.ARABIC]: skipLabel
        ? 'أدخل رقم الواتساب'
        : 'مثل: 9876543210',
      [LANGUAGE_VALUES.FRENCH]: skipLabel
        ? 'Entrez votre numéro WhatsApp'
        : 'ex: 9876543210',
      [LANGUAGE_VALUES.SPANISH]: skipLabel
        ? 'Introduce tu número de WhatsApp'
        : 'ej: 9876543210',
      [LANGUAGE_VALUES.MALAY]: skipLabel
        ? 'Masukkan nombor WhatsApp anda'
        : 'cth: 9876543210',
    };
    return (
      placeholders[language] ||
      (skipLabel ? 'Enter whatsapp number' : 'eg: 9876543210')
    );
  }

  getWhatsappNumberTitle(language: LANGUAGE_VALUES) {
    const titles = {
      [LANGUAGE_VALUES.ARABIC]: 'رقم الواتساب',
      [LANGUAGE_VALUES.FRENCH]: 'Numéro WhatsApp',
      [LANGUAGE_VALUES.SPANISH]: 'Número de WhatsApp',
      [LANGUAGE_VALUES.MALAY]: 'Nombor WhatsApp',
    };
    return titles[language] || 'Whatsapp Number';
  }

  getCopiedMessage(language: LANGUAGE_VALUES) {
    return this.getValidationMessage(
      language,
      {
        [LANGUAGE_VALUES.ARABIC]: 'تم النسخ',
        [LANGUAGE_VALUES.FRENCH]: 'Copié',
        [LANGUAGE_VALUES.SPANISH]: 'Copiado',
        [LANGUAGE_VALUES.MALAY]: 'Disalin',
      },
      'Copied!',
    );
  }

  getPrivacyPolicyText(language: LANGUAGE_VALUES) {
    const translations = {
      [LANGUAGE_VALUES.ARABIC]: 'لقد قرأت ووافقت على',
      [LANGUAGE_VALUES.FRENCH]: "J'ai lu et accepté la",
      [LANGUAGE_VALUES.SPANISH]: 'He leído y acepto la',
      [LANGUAGE_VALUES.MALAY]: 'Saya telah membaca dan bersetuju dengan',
    };
    return translations[language] || 'I have read and agree to the';
  }

  getPrivacyPolicyLabel(language: LANGUAGE_VALUES) {
    const labels = {
      [LANGUAGE_VALUES.ARABIC]: 'سياسة الخصوصية',
      [LANGUAGE_VALUES.FRENCH]: 'politique de confidentialité',
      [LANGUAGE_VALUES.SPANISH]: 'política de privacidad',
      [LANGUAGE_VALUES.MALAY]: 'dasar privasi',
    };
    return labels[language] || 'Privacy Policy';
  }
}
